import { TreeView, Change, TraitLabel, StableRange, StablePlace, SharedTree } from '@fluid-experimental/tree';
import { MfsItem, AppDataOf, MfsItemId, mfsError } from '../..';
import { getItemNode } from './nodesRead';

import { createMfsItemNode } from './nodesWrite';
import { ITEMS, NodeKind } from './types';
import { removeAllReverseLinks } from './relationships';
import { setProperty } from './propertiesWrite';
import { getPropertyKey } from '../../store/mfsAccessControl';
import { logCustomMetric } from '../../log';
import { logVerbose } from '../../log';

/** @returns The {@link Change} array that inserts the `item` to the tree `view`. */
export function insertMfsItem<T extends MfsItem>(tree: SharedTree, view: TreeView, item: T): Change[] {
  const newItemNode = createMfsItemNode(item, tree);
  const stableNodeId = tree.convertToStableNodeId(newItemNode.identifier);
  // The Harmony team's webpack bundling modifies the functionality of the convertToStableNodeId function, resulting in a mangled ID. The code below verifies that the stableNodeId generated is not mangled. If it is, we log the error to our telemetry and throw an mfsError to prevent the mangled ID from entering the shared tree.
  try {
    tree.convertToNodeId(stableNodeId);
  } catch {
    logCustomMetric(
      'invalidStableNodeId',
      'invalidStableNodeId',
      { compressedId: newItemNode.identifier, invalidStableNodeId: stableNodeId },
      'insertMfsItem'
    );
    throw mfsError.invalidStableNodeId(stableNodeId, newItemNode.identifier.NodeId);
  }

  const newIndexNode = {
    definition: NodeKind.Index,
    payload: stableNodeId,
  };

  return [
    ...Change.insertTree([newItemNode], StablePlace.atEndOf({ parent: view.root, label: ITEMS })),
    ...Change.insertTree([newIndexNode], StablePlace.atStartOf({ parent: view.root, label: newItemNode.payload.id })),
  ];
}

/** @returns The {@link Change} array that updates the item with `itemId` with the given `patch` on the tree `view`. */
export function updateMfsItem<T extends MfsItem>(
  tree: SharedTree,
  view: TreeView,
  itemId: MfsItemId,
  patch: Partial<AppDataOf<T>>
): Change[] {
  logVerbose('updateMfsItem', 'Updating Mfs item', { root: view.root, itemId });
  const changes: Change[] = [];

  const itemNode = getItemNode(tree, view, itemId);

  for (const [propertyKey, propertyValue] of Object.entries(patch)) {
    const propertyTrait = getPropertyKey(propertyKey, 'set') as TraitLabel;
    changes.push(...setProperty(view, itemNode, propertyTrait, propertyValue));
  }

  return changes;
}

/** @returns The changes to remove the given item from the given view. */
export function deleteMfsItem(tree: SharedTree, view: TreeView, itemId: MfsItemId): Change[] {
  logVerbose('deleteMfsItem', 'Deleting Mfs item', { root: view.root, itemId });
  const itemNode = getItemNode(tree, view, itemId);

  // remove the reverse links that are not descendants of the deleted item node.
  const changes = removeAllReverseLinks(tree, view, itemId, itemNode.identifier);

  // remove the item node, this removes all forward links & properties.
  changes.push(Change.delete(StableRange.only(itemNode.identifier)));

  // remove item id from index.
  changes.push(Change.delete(StableRange.all({ parent: view.root, label: itemId as unknown as TraitLabel })));

  // TODO: (denizc) add work item - wait for GC'ed refs because this will be too expensive (remove item from all collections that reference it).
  return changes;
}

/** @returns The changes to remove all mfs items from the given view. */
export function deleteAllMfsItems(view: TreeView): Change[] {
  logVerbose('deleteAllMfsItems', 'Deleting all Mfs items', { root: view.root });
  // TODO: (dc) ask if it's possible to expose view.rootNode - it's actually a member of view & we don't need to look it up again.
  const rootNode = view.getViewNode(view.root);
  const changes: Change[] = [];

  for (const label of rootNode.traits.keys()) {
    changes.push(Change.delete(StableRange.all({ parent: view.root, label })));
  }

  return changes;
}
